import React, { useState, useEffect, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { formatDateRu } from '../utils/formatUtils';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  User,
  Search,
  Filter,
  Settings,
  Play,
  Pause,
  Eye,
  RefreshCw,
  AlertTriangle,
  UserCircle
} from 'lucide-react';
import axios from 'axios';
import Pagination from './Pagination';

import { getBackendUrl } from '../utils/api';

const API = `${getBackendUrl()}/api`;

const TradingAccountsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [balanceFilter, setBalanceFilter] = useState('');
  // [TA-2026-05-14] Filter: clients with open trades / without / all (combinable with other filters)
  const [tradesFilter, setTradesFilter] = useState('');
  // [TA-MANAGER-2026-08-03] Filter accounts by assigned sales/retention manager.
  const [managerFilter, setManagerFilter] = useState('');
  const [managers, setManagers] = useState([]);
  // [TA-2026-05-14] Sort state — click column header to toggle
  const [sortField, setSortField] = useState('last_activity');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeAccounts: 0,
    totalBalance: 0,
    totalProfit: 0,
    activeTrades: 0
  });

  const ITEMS_PER_PAGE = 25;

  const statusOptions = [
    { value: '', label: tt('Все статусы') },
    { value: 'active', label: tt('Активные') },
    { value: 'blocked', label: tt('Заблокированные') },
    { value: 'demo', label: tt('Демо') }
  ];

  const balanceOptions = [
    { value: '', label: tt('Все балансы') },
    { value: 'zero', label: tt('Нулевые ($0)') },
    { value: 'low', label: tt('Низкие ($1-$100)') },
    { value: 'medium', label: tt('Средние ($100-$1000)') },
    { value: 'high', label: tt('Высокие ($1000+)') }
  ];

  const tradesOptions = [
    { value: '', label: tt('Все клиенты') },
    { value: 'has_open', label: tt('С открытыми сделками') },
    { value: 'no_open', label: tt('Без открытых сделок') }
  ];

  // [TA-2026-05-14] Click any sortable header to toggle direction; new column resets to desc
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  useEffect(() => {
    fetchTradingAccounts();
    // [TA-MANAGER-2026-08-03b] Preload ALL staff (any role except client) for filter.
    (async () => {
      try {
        const res = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const list = (res.data || []).filter((u) => u.role && String(u.role).toLowerCase() !== 'client');
        setManagers(list);
      } catch (e) {
        console.warn('Failed to load managers list', e);
      }
    })();
  }, []);

  const fetchTradingAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trading/admin/accounts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('✅ Trading accounts fetched:', response.data);
      setTradingAccounts(response.data.accounts || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('❌ Error fetching trading accounts:', error);
    } finally {
      setLoading(false);
    }
  };


  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, balanceFilter, tradesFilter, managerFilter, sortField, sortDirection]);


  const handleQuickAction = async (accountId, action, value = null) => {
    try {
      const endpoint = `${API}/trading/admin/accounts/${accountId}/${action}`;
      const payload = value !== null ? { value } : {};
      
      await axios.post(endpoint, payload, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log(`✅ Quick action ${action} completed for account ${accountId}`);
      fetchTradingAccounts(); // Refresh data
    } catch (error) {
      console.error(`❌ Error performing ${action}:`, error);
      alert(`${tt('Ошибка выполнения операции:')} ${error.response?.data?.detail || error.message}`);
    }
  };

  const filteredAccounts = tradingAccounts.filter(account => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      (account.client_name && account.client_name.toLowerCase().includes(searchLower)) ||
      (account.client_full_name && account.client_full_name.toLowerCase().includes(searchLower)) ||
      (account.client_email && account.client_email.toLowerCase().includes(searchLower)) ||
      (account.client_id && account.client_id.toString().includes(searchTerm));
    
    const matchesStatus = statusFilter === '' || account.status === statusFilter;
    
    let matchesBalance = true;
    if (balanceFilter === 'zero') matchesBalance = account.balance === 0;
    else if (balanceFilter === 'low') matchesBalance = account.balance > 0 && account.balance <= 100;
    else if (balanceFilter === 'medium') matchesBalance = account.balance > 100 && account.balance <= 1000;
    else if (balanceFilter === 'high') matchesBalance = account.balance > 1000;

    // [TA-2026-05-14] New filter: clients with/without open trades (options + CFD)
    const openCount = account.active_trades_count || 0;
    let matchesTrades = true;
    if (tradesFilter === 'has_open') matchesTrades = openCount > 0;
    else if (tradesFilter === 'no_open') matchesTrades = openCount === 0;

    // [TA-MANAGER-2026-08-03] Filter by assigned manager. Accepts multiple
    // shapes (int vs string id) since assigned_manager can be stored either
    // way historically.
    let matchesManager = true;
    if (managerFilter) {
      const mf = String(managerFilter);
      const am = account.assigned_manager;
      matchesManager = am != null && String(am) === mf;
    }

    return matchesSearch && matchesStatus && matchesBalance && matchesTrades && matchesManager;
  }).sort((a, b) => {
    // [TA-2026-05-14] Sortable columns
    const dir = sortDirection === 'asc' ? 1 : -1;
    const num = (v) => (v == null ? -Infinity : Number(v) || 0);
    switch (sortField) {
      case 'last_activity': {
        const av = a.last_activity ? new Date(a.last_activity).getTime() : 0;
        const bv = b.last_activity ? new Date(b.last_activity).getTime() : 0;
        return (av - bv) * dir;
      }
      case 'active_trades':
        return (num(a.active_trades_count) - num(b.active_trades_count)) * dir;
      case 'active_volume':
        return (num(a.active_trades_volume) - num(b.active_trades_volume)) * dir;
      case 'balance':
        return (num(a.balance) - num(b.balance)) * dir;
      case 'profit':
        return (num(a.total_profit) - num(b.total_profit)) * dir;
      case 'client_id':
        return (num(a.client_id) - num(b.client_id)) * dir;
      default:
        return 0;
    }
  });

  // Pagination
  const paginatedAccounts = filteredAccounts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'blocked': return 'text-red-600 bg-red-100';
      case 'demo': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Play className="w-4 h-4" />;
      case 'blocked': return <Pause className="w-4 h-4" />;
      case 'demo': return <Settings className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">{tt('Торговые аккаунты')}</h1>
          <p className="page-subtitle">{tt('Управление торговыми счетами клиентов')}</p>
        </div>
        <button
          onClick={fetchTradingAccounts}
          className="btn btn-primary flex items-center space-x-2"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>{tt('Обновить')}</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center">
            <User className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.totalAccounts}</div>
              <div className="text-sm text-gray-600">{tt('Всего аккаунтов')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <Play className="w-8 h-8 text-green-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.activeAccounts}</div>
              <div className="text-sm text-gray-600">{tt('Активные')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-purple-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalBalance)}</div>
              <div className="text-sm text-gray-600">{tt('Общий баланс')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-emerald-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.totalProfit)}</div>
              <div className="text-sm text-gray-600">{tt('Общая прибыль')}</div>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <TrendingDown className="w-8 h-8 text-orange-500 mr-3" />
            <div>
              <div className="text-2xl font-bold text-orange-600">{stats.activeTrades}</div>
              <div className="text-sm text-gray-600">{tt('Активные сделки')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder={tt('Поиск по ФИО, email или ID...')}
              className="input pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="ta-search-input"
            />
          </div>

          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="ta-status-filter"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="input"
            value={balanceFilter}
            onChange={(e) => setBalanceFilter(e.target.value)}
            data-testid="ta-balance-filter"
          >
            {balanceOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="input"
            value={tradesFilter}
            onChange={(e) => setTradesFilter(e.target.value)}
            data-testid="ta-trades-filter"
          >
            {tradesOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* [TA-MANAGER-2026-08-03] Manager filter */}
          <select
            className="input"
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            data-testid="ta-manager-filter"
          >
            <option value="">{tt('Все менеджеры')}</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setBalanceFilter('');
              setTradesFilter('');
              setManagerFilter('');
              setSortField('last_activity');
              setSortDirection('desc');
            }}
            className="btn btn-secondary flex items-center space-x-2"
            data-testid="ta-clear-filters"
          >
            <Filter className="w-4 h-4" />
            <span>{tt('Очистить')}</span>
          </button>
        </div>

        {/* [TA-2026-05-14] Active filter summary — combined AND logic */}
        {(searchTerm || statusFilter || balanceFilter || tradesFilter || managerFilter) && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="text-gray-500">{tt('Активные фильтры')}:</span>
            {searchTerm && <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{tt('Поиск')}: {searchTerm}</span>}
            {statusFilter && <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{statusOptions.find(o => o.value === statusFilter)?.label}</span>}
            {balanceFilter && <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{balanceOptions.find(o => o.value === balanceFilter)?.label}</span>}
            {tradesFilter && <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded">{tradesOptions.find(o => o.value === tradesFilter)?.label}</span>}
            <span className="text-gray-500 ml-auto">{tt('Найдено')}: {filteredAccounts.length}</span>
          </div>
        )}
      </div>

      {/* Trading Accounts Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="spinner mr-3"></div>
            <span className="text-gray-600">{tt('Загрузка торговых аккаунтов...')}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('client_id')} data-testid="ta-sort-client">
                    {tt('Клиент')}<SortIcon field="client_id" />
                  </th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('balance')} data-testid="ta-sort-balance">
                    {tt('Баланс')}<SortIcon field="balance" />
                  </th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('profit')} data-testid="ta-sort-profit">
                    {tt('Прибыль')}<SortIcon field="profit" />
                  </th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('active_volume')} data-testid="ta-sort-trades">
                    {tt('Активные сделки')}<SortIcon field="active_volume" />
                  </th>
                  <th>{tt('Статус')}</th>
                  <th className="cursor-pointer select-none" onClick={() => handleSort('last_activity')} data-testid="ta-sort-activity">
                    {tt('Последняя активность')}<SortIcon field="last_activity" />
                  </th>
                  <th>{tt('Действия')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-500">
                      <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>{tt('Торговые аккаунты не найдены')}</p>
                    </td>
                  </tr>
                ) : (
                  paginatedAccounts.map((account) => (
                    <tr key={account.id} className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={(e) => {
                        // [CFD-2026-05] Whole row clickable — but ignore clicks on action buttons / links
                        if (e.target.closest('button, a')) return;
                        navigate(`/crm/trading-accounts/${account.id}`);
                      }}
                      data-testid={`trading-account-row-${account.id}`}
                    >
                      <td>
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-2 text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900">{account.client_full_name || account.client_name || tt('Без имени')}</div>
                            <div className="text-sm text-gray-500">ID: {account.client_id}</div>
                            <div className="text-xs text-gray-400">{account.client_email}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td>
                        <div className="font-medium">{formatCurrency(account.balance)}</div>
                        <div className="text-sm text-gray-500">
                          {tt('Всего инвестировано')}: {formatCurrency(account.total_invested)}
                        </div>
                      </td>
                      
                      <td>
                        <div className={`font-medium ${account.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(account.total_profit)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {account.total_trades} {tt('сделок')}
                        </div>
                      </td>
                      
                      <td>
                        <div className="font-medium text-orange-600" data-testid={`active-trades-volume-${account.id}`}>
                          {formatCurrency(account.active_trades_volume || 0)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {account.active_trades_count || 0} {tt('сделок')}
                        </div>
                      </td>
                      
                      <td>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(account.status)}`}>
                          {getStatusIcon(account.status)}
                          <span className="ml-1">
                            {account.status === 'active' ? tt('Активен') :
                             account.status === 'blocked' ? tt('Заблокирован') :
                             account.status === 'demo' ? tt('Демо') : tt('Неизвестно')}
                          </span>
                        </span>
                      </td>
                      
                      <td>
                        <div className="text-sm text-gray-900">
                          {account.last_activity ? formatDateRu(account.last_activity) : tt('Никогда')}
                        </div>
                      </td>
                      
                      <td>
                        <div className="flex space-x-2">
                          <Link
                            to={`/crm/trading-accounts/${account.id}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title={tt('Подробнее о торговом счёте')}
                            data-testid={`ta-view-account-${account.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Link>

                          {/* [TA-CLIENT-CARD-2026-08-03] Quick jump to CRM client card */}
                          {account.client_id && (
                            <Link
                              to={`/crm/clients/${account.client_id}`}
                              className="text-purple-600 hover:text-purple-900 transition-colors"
                              title={tt('Карточка клиента')}
                              data-testid={`ta-open-client-${account.id}`}
                            >
                              <UserCircle className="w-4 h-4" />
                            </Link>
                          )}
                          
                          {account.status === 'active' ? (
                            <button
                              onClick={() => handleQuickAction(account.id, 'block')}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title={tt('Заблокировать торговлю')}
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleQuickAction(account.id, 'activate')}
                              className="text-green-600 hover:text-green-900 transition-colors"
                              title={tt('Активировать торговлю')}
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={filteredAccounts.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TradingAccountsPage;